import { AuthClient, UserAuthToken } from "../../../domain/auth/user-auth/auth-client";
import { getUserPersonalData } from "../../../domain/io/user-data/fetch-user-personal-data";
import { pipe } from "fp-ts/function";
import { either } from "fp-ts";
import { UserAspectAction, UserAspectState } from "./aspect-types";
import { useResourceRelatedInfo } from "../../../hooks/use-resource-related-info";
import { LoadingStateOps } from "../../../domain/data-layer/loading-state";
import { LoadingStateWithIdleOps } from "../../../domain/data-layer/loading-state-with-idle";
import * as E from "fp-ts/lib/Either";
import { simpleRetry } from "./simple-retry";
import { pushableEventOfPure } from "../../../domain/google-tag-manager/pushable";

const fetchUserPersonalData = (userAuthToken: UserAuthToken) => simpleRetry(() => getUserPersonalData(userAuthToken));
const fetchSalesForceUserProfile = (userAuthToken: UserAuthToken) =>
  simpleRetry(() => AuthClient.fetchSalesforceUserProfile(userAuthToken));

export function useAsyncDataFetches(state: UserAspectState, dispatch: React.Dispatch<UserAspectAction>) {
  useResourceRelatedInfo(
    state ? LoadingStateOps.asLoaded(state) : LoadingStateWithIdleOps.idleS,
    _ => _.tag + _.authToken.access_token,
    resource => (resource.tag === "logged-in-user" ? E.right(resource) : E.left("anonymous-user")),
    current => {
      const retriedUserPersonalDataFetch = fetchUserPersonalData(current.authToken);
      const retriedSalesForceUserProfileFetch = fetchSalesForceUserProfile(current.authToken);

      const p1 = retriedUserPersonalDataFetch()
        .then(userPersonalData => dispatch({ type: "user-ctp-personal-data-loaded", value: userPersonalData }))
        .catch(err => {
          dispatch({ type: "user-ctp-personal-data-failed-to-load" });
        });

      const p2 = retriedSalesForceUserProfileFetch()
        .then(eitherUserPersonalData => {
          pipe(
            eitherUserPersonalData,
            either.fold(
              err => {
                console.error(err);

                dispatch({ type: "user-sf-personal-data-failed-to-load" });
              },
              userPersonalData => {
                dispatch({ type: "user-sf-personal-data-loaded", value: userPersonalData });
              },
            ),
          );
        })
        .catch(e => {
          return dispatch({ type: "user-sf-personal-data-failed-to-load" });
        });

      return Promise.allSettled([p1, p2]);
    },
  );
}
